// Phase 8.5b: a track-first "Track input" selector. The primary picker is the
// product-level track/layout list (GET /api/track_inputs) — searchable and
// grouped Current-2026-first — and selecting a track reveals its source
// campaigns (recommended default + alternates). Choosing a source sets the
// concrete source_event, which the existing cached-event detail panel and the
// create flow resolve against unchanged (GET /api/cached_events stays the
// lower-level inventory). Unavailable / pre-event tracks are visible but not
// creatable (no concrete source_event).
import { useEffect, useState, type FormEvent } from "react";
import { createTest, fetchTrackReplayStatus } from "../api/tests";
import { listCachedEvents, ingestCachedEvent } from "../api/cached_events";
import {
  fetchTrackInputRecipe,
  listTrackInputs,
  type StudyRecipe,
} from "../api/track_inputs";
import type { CachedEvent, TrackInput, TrackInputSource } from "../api/schemas";
import { InputCard } from "./study/InputCard";
import { fetchTireCatalog, type TireModel } from "../api/tireCatalog";

export type NewTestWizardProps = {
  // Phase 1B: callback receives just the new Test's id. TestDetailView
  // refetches via GET /api/tests/{id} on its own so the wizard's
  // create-response shape is decoupled from the detail view's needs.
  onCreated: (testId: number) => void;
};

type EventsState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; events: CachedEvent[] };

function eventReadinessLabel(ev: CachedEvent): string {
  if (ev.study_ready) return "STUDY READY";
  if (ev.event_class === "rig_characterisation") return "RIG CHARACTERISATION";
  if (ev.event_class === "unknown_prefix") return "UNKNOWN PREFIX";
  if (ev.gate_status === "UNKNOWN") return "GATE UNKNOWN";
  return `GATE ${ev.gate_status}`;
}

function labDisclosureReason(ev: CachedEvent): string {
  const parts = [
    ev.event_class !== "track" ? ev.event_class.replace("_", " ") : null,
    ev.gate_status !== "PASS" ? `gate ${ev.gate_status}` : null,
    !ev.selectable ? ev.rejection_reasons.join("; ") : null,
  ].filter(Boolean);
  return parts.join(" · ") || "not Study-ready";
}

// Confidence state → engineer-facing label. selected_under_investigation and
// degraded are surfaced as metadata (a source carrying them is NOT disabled).
const CONFIDENCE_LABEL: Record<string, string> = {
  gate_clean: "gate clean",
  selected_under_investigation: "under investigation",
  trusted_unverified: "trusted · unverified",
  degraded: "degraded",
};

// One chip, three honest states (Joe 2026-06-10 de-clutter): the chip says
// what STUDY you'll get, not the data's validation history.
//   (none)      — measured 7-post rig data; R0 compares against the real rig.
//   "replay"    — a recorded track lap drives the rig; no R² exists.
//   "synthetic" — class-profile lap (statistically modelled); no R².
//   "data pending" — venue not yet runnable on this deployment.
// Gate/trust confidence is engineering metadata — it lives in the study
// detail's inputs audit, never in the picker.
function trackTierChip(t: TrackInput): { label: string; cls: string } | null {
  // A venue with an ingested lap shows "replay" whether or not it ALSO has a
  // 7-post recording — the chip advertises that a recorded lap can drive the
  // rig here (dual-source venues like Mid-Ohio offer both; the wizard's
  // "drive from track-replay lap" checkbox picks, 2026-07-02).
  if (t.replay_available && t.runnable && t.tier !== "synthetic_candidate") {
    return { label: "replay", cls: "ti-tier-replay" };
  }
  if (t.runnable && t.recommended_default) return null;
  if (t.tier === "synthetic_candidate" && t.runnable) {
    return { label: "synthetic", cls: "ti-tier-synthetic" };
  }
  return { label: "data pending", cls: "ti-tier-unavailable" };
}

function trackSourceMessage(t: TrackInput): string {
  const base = t.tier === "pre_event_no_reference"
    ? "Not yet raced — no rig source exists for this venue yet."
    : "No cached 7-post source on this deployment.";
  return t.warnings[0] ? `${base} ${t.warnings[0]}` : base;
}

function TrackInputRow(
  { t, selected, onSelect }: { t: TrackInput; selected: boolean; onSelect: () => void },
) {
  const chip = trackTierChip(t);
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      className={`ti-track-row${selected ? " selected" : ""}`}
      data-testid={`ti-track-${t.key}`}
      onClick={onSelect}
    >
      <span className="ti-track-name">{t.display_name}</span>
      <span className={`ti-layout-badge ti-layout-${t.layout}`}>{t.layout}</span>
      {chip && <span className={`ti-chip ${chip.cls}`}>{chip.label}</span>}
    </button>
  );
}

function SourceRow(
  { src, isDefault, selected, cached, onSelect }: {
    src: TrackInputSource;
    isDefault: boolean;
    selected: boolean;
    cached: CachedEvent | undefined;
    onSelect: () => void;
  },
) {
  // Selectable iff the source resolves to a selectable cached event. Confidence
  // (under-investigation / degraded) is metadata, NOT a reason to disable.
  const resolvable = cached != null && cached.selectable;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={!resolvable}
      className={`ti-source-row${selected ? " selected" : ""}`}
      data-testid={`ti-source-${src.source_event}`}
      title={cached ? `gate ${cached.gate_status} · ${cached.recording_count} rec` : "not cached on this deployment"}
      onClick={onSelect}
    >
      <span className="mono ti-source-id">{src.source_event}</span>
      {isDefault && <span className="ti-default-tag">recommended</span>}
      <span className={`ti-chip ti-conf-${src.confidence_state}`}>
        {CONFIDENCE_LABEL[src.confidence_state] ?? src.confidence_state}
      </span>
      {!resolvable && (
        <span className="muted ti-source-note">{cached ? "not selectable here" : "not cached here"}</span>
      )}
      {src.warnings.map((w) => (
        <span key={w} className="ti-source-warn muted">{w}</span>
      ))}
    </button>
  );
}

export function NewTestWizard({ onCreated }: NewTestWizardProps) {
  const [eventsState, setEventsState] = useState<EventsState>({ kind: "loading" });
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  // Phase 8.5b: the track-first selector + its search box. selectedTrackKey is
  // the view-model identity (e.g. "LBE#street"); the concrete source_event still
  // lives in selectedEventId, which everything downstream resolves against.
  const [trackInputs, setTrackInputs] = useState<TrackInput[] | null>(null);
  const [selectedTrackKey, setSelectedTrackKey] = useState<string>("");
  // Fable P3 recipe rail: the server-resolved four-input recipe for the
  // selected venue. Best-effort — absence just keeps the legacy card text.
  const [recipe, setRecipe] = useState<StudyRecipe | null>(null);
  const [trackSearch, setTrackSearch] = useState<string>("");
  const [studyName, setStudyName] = useState<string>("");
  const [studyNameEdited, setStudyNameEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Layer 2: per-event ingest progress + error scoped to the selected
  // event_id so the button shows the right state and the error line
  // doesn't bleed into a different row after re-selection.
  const [ingestingId, setIngestingId] = useState<string | null>(null);
  const [ingestError, setIngestError] = useState<{ eventId: string; message: string } | null>(null);
  // Item D: track codes (upper-cased, the cache key) with an ingested replay
  // lap; lets the engineer drive the rig from the lap instead of the cached
  // 7-post recording. Best-effort — absence just hides the option.
  const [replayTracks, setReplayTracks] = useState<Set<string>>(new Set());
  // Phase 4: deployment-level replay availability. False on cloud (no laps
  // baked) — drives an explicit "unavailable on this deployment" note instead
  // of silently omitting the mode.
  const [replayGloballyAvailable, setReplayGloballyAvailable] = useState(true);
  const [useReplay, setUseReplay] = useState(false);
  // Slice 3: optional create-time tyre override. "" = event-derived default.
  // Catalog load is best-effort; failure leaves the list empty (default only).
  const [tyreModels, setTyreModels] = useState<TireModel[]>([]);
  const [tyreType, setTyreType] = useState<string>("");
  // Lever-set v1 (2026-07-12): opt AutoResearch into extra levers beyond
  // dampers, written to kpi_targets.ar_levers. Off by default = dampers-only
  // (the unchanged action space). Each widens what AutoResearch may vary
  // per candidate.
  const [allowTyreLever, setAllowTyreLever] = useState(false);
  const [allowThirdGapLever, setAllowThirdGapLever] = useState(false);
  // Fable P4: the staged own-event setup backing a replay-only Study
  // (kpi_targets.setup_file). "" when the venue has no staged setups —
  // the donor flow applies instead.
  const [selectedSetupId, setSelectedSetupId] = useState<string>("");

  useEffect(() => {
    fetchTireCatalog()
      .then(setTyreModels)
      .catch(() => setTyreModels([]));
  }, []);

  useEffect(() => {
    fetchTrackReplayStatus()
      .then((status) => {
        // Membership by BOTH the cache-dir code (MOH) and its campaign
        // MAT-prefix aliases (mid) — a cached EVENT carries the campaign
        // code, which differs from the cache dir at Mid-Ohio + Laguna.
        setReplayTracks(
          new Set(
            status.tracks.flatMap((t) => [
              t.track_code.toUpperCase(),
              ...t.campaign_codes.map((c) => c.toUpperCase()),
            ]),
          ),
        );
        setReplayGloballyAvailable(status.available);
      })
      .catch(() => {
        setReplayTracks(new Set());
        setReplayGloballyAvailable(false);
      });
  }, []);

  useEffect(() => {
    listCachedEvents()
      .then((events) => {
        setEventsState({ kind: "ready", events });
      })
      .catch((e) => {
        setEventsState({
          kind: "error",
          message: e instanceof Error ? e.message : String(e),
        });
      });
  }, []);

  useEffect(() => {
    listTrackInputs()
      .then(setTrackInputs)
      .catch(() => setTrackInputs([]));
  }, []);

  // Default selection: the first runnable, demanded track + its recommended
  // source — picked once both the track list and the cached inventory are in.
  useEffect(() => {
    if (selectedTrackKey || trackInputs == null || eventsState.kind !== "ready") return;
    const first = trackInputs.find((t) => t.demanded && t.runnable && t.recommended_default);
    if (first?.recommended_default) {
      setSelectedTrackKey(first.key);
      setSelectedEventId(first.recommended_default.source_event);
      setStudyName((cur) => cur || first.recommended_default!.source_event);
    }
  }, [trackInputs, eventsState, selectedTrackKey]);

  const selectedEvent =
    eventsState.kind === "ready"
      ? eventsState.events.find((e) => e.event_id === selectedEventId) ?? null
      : null;
  const replayAvailable =
    selectedEvent != null
    && replayTracks.has(selectedEvent.track_code.toUpperCase());
  // Runnable/warmable non-track events stay accessible as explicit lab mode.
  // This includes rig-characterisation inputs (fri/s4/etc.), FAIL/NOT_OK gate
  // rows, UNKNOWN rows, and needs_ingest rows.
  const labEvents =
    eventsState.kind === "ready"
      ? eventsState.events.filter(
          (e) =>
            !e.study_ready
            && (e.selectable || e.ingest_status === "needs_ingest"),
        )
      : [];

  function chooseEvent(eventId: string) {
    setSelectedEventId(eventId);
    if (!studyNameEdited) setStudyName(eventId);
  }

  const selectedTrack =
    trackInputs?.find((t) => t.key === selectedTrackKey) ?? null;
  // Fable P2: a replay-only track (ingested lap, no cached 7-post source).
  // Rig actuation comes from the lap; the engineer picks a SETUP DONOR
  // campaign whose XLSM/spec supplies the car platform.
  const replayOnlyTrack =
    selectedTrack != null
    && (selectedTrack.tier === "track_replay_inverse"
      || selectedTrack.tier === "synthetic_candidate")
    && selectedTrack.runnable
    && selectedTrack.track_code != null;
  // Fable P4: the venue's own staged setups (server default-pick order).
  // Non-empty ⇒ no donor required — the own setup IS the car platform.
  const ownSetups = (replayOnlyTrack && selectedTrack?.own_setups) || [];
  const ownSetupTrack = replayOnlyTrack && ownSetups.length > 0;
  const donorCandidates =
    eventsState.kind === "ready" && replayOnlyTrack && !ownSetupTrack
      ? eventsState.events.filter((e) => e.study_ready && e.selectable)
      : [];
  // Default-donor policy (Joe 2026-06-10: reliable inputs everywhere —
  // sensible defaults preselected, overrides available). Same-layout donors
  // first (layout via the selector rows), newest campaign first within a
  // group; auto-select so create is never blocked on an empty donor.
  const layoutByCode = new Map(
    (trackInputs ?? [])
      .filter((t) => t.track_code)
      .map((t) => [t.track_code!.toLowerCase(), t.layout]),
  );
  useEffect(() => {
    if (!replayOnlyTrack || donorCandidates.length === 0) return;
    if (donorCandidates.some((e) => e.event_id === selectedEventId)) return;
    const wantLayout = selectedTrack?.layout;
    const ranked = [...donorCandidates].sort((a, b) => {
      const aSame = layoutByCode.get(a.track_code.toLowerCase()) === wantLayout ? 0 : 1;
      const bSame = layoutByCode.get(b.track_code.toLowerCase()) === wantLayout ? 0 : 1;
      if (aSame !== bSame) return aSame - bSame;
      return b.event_id.localeCompare(a.event_id);   // newest campaign first
    });
    chooseEvent(ranked[0].event_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayOnlyTrack, selectedTrackKey, donorCandidates.length]);
  // Fable P4: default the own-setup pick (first = server default-pick order)
  // and default the Study name from the setup's event label.
  useEffect(() => {
    if (!ownSetupTrack) {
      setSelectedSetupId("");
      return;
    }
    if (ownSetups.some((s) => s.setup_id === selectedSetupId)) return;
    const pick = ownSetups[0];
    setSelectedSetupId(pick.setup_id);
    if (!studyNameEdited) {
      setStudyName(pick.event_label || selectedTrack?.track_code || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownSetupTrack, selectedTrackKey, ownSetups.length]);
  const selectedTrackSources: TrackInputSource[] = selectedTrack
    ? ([selectedTrack.recommended_default, ...selectedTrack.alternates].filter(
        Boolean,
      ) as TrackInputSource[])
    : [];
  const eventById = (se: string): CachedEvent | undefined =>
    eventsState.kind === "ready"
      ? eventsState.events.find((e) => e.event_id === se)
      : undefined;
  // True when at least one of the selected track's sources resolves to a
  // selectable cached event on THIS deployment. False both for unavailable /
  // pre-event tracks (no sources) AND for a track whose policy default exists
  // but isn't cached/selectable here (the runtime-bundle-miss case, e.g. NAS
  // on cloud) — both must block create with an explanation.
  const selectableSourceExists = selectedTrackSources.some((s) => {
    const ce = eventById(s.source_event);
    return ce != null && ce.selectable;
  });
  const trackQuery = trackSearch.trim().toLowerCase();
  const matchesTrackQuery = (t: TrackInput) =>
    !trackQuery
    || t.display_name.toLowerCase().includes(trackQuery)
    || (t.track_code ?? "").toLowerCase().includes(trackQuery)
    || t.layout.toLowerCase().includes(trackQuery);
  const currentTracks = (trackInputs ?? []).filter((t) => t.demanded && matchesTrackQuery(t));
  const validationTracks = (trackInputs ?? []).filter((t) => !t.demanded && matchesTrackQuery(t));

  function chooseTrack(t: TrackInput) {
    setSelectedTrackKey(t.key);
    if (t.recommended_default) {
      chooseEvent(t.recommended_default.source_event);
    } else {
      setSelectedEventId("");
    }
  }

  useEffect(() => {
    if (!selectedTrackKey) {
      setRecipe(null);
      return;
    }
    let stale = false;
    fetchTrackInputRecipe(selectedTrackKey)
      .then((r) => { if (!stale) setRecipe(r); })
      .catch(() => { if (!stale) setRecipe(null); });
    return () => { stale = true; };
  }, [selectedTrackKey]);

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (ownSetupTrack) {
      if (!selectedSetupId) {
        setError("Pick the venue's own setup sheet to start a Study.");
        return;
      }
    } else if (!selectedEvent || !selectedEvent.selectable) {
      setError("Pick a selectable cached source to start a Study.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const test = await createTest({
        // Fable P2: a replay-only Study is keyed to the REPLAY track; the
        // donor campaign's own track_code only identifies the setup source.
        // The API's track_code pattern is lowercase (^[a-z0-9]{2,8}$) while
        // the selector manifest carries canonical UPPER codes — lowercase on
        // the wire (the replay-cache lookup uppercases internally).
        track_code:
          replayOnlyTrack && selectedTrack?.track_code
            ? selectedTrack.track_code.toLowerCase()
            : selectedEvent!.track_code,
        replay_recording_id: null,
        baseline_setup_name: null,
        kpi_targets: {
          // Slice 4: the Study objective is chosen AFTER the reference run
          // (via AutoResearch), not forced at create. R0 is created with no
          // objective target; the evaluation endpoint resolves it post-R0.
          // Item D: drive the rig from the ingested track lap (the campaign
          // source_event still supplies the setup XLSM + spec).
          // Fable P2: replay-only tracks make replay implicit. Fable P4: the
          // venue's OWN staged setup (kpi_targets.setup_file) beats the donor
          // — no source_event; else stamp the donor for provenance.
          ...(replayOnlyTrack && selectedTrack?.track_code
            ? {
                track_replay: {
                  enabled: true,
                  track_code: selectedTrack.track_code.toLowerCase(),
                },
                ...(ownSetupTrack
                  ? { setup_file: selectedSetupId }
                  : {
                      setup_donor: {
                        source_event: selectedEvent!.event_id,
                        reason:
                          "replay-only track: donor campaign supplies the setup "
                          + "XLSM + spec; rig actuation from the ingested lap",
                      },
                    }),
              }
            : useReplay && replayAvailable
              ? {
                  track_replay: {
                    enabled: true,
                    track_code: selectedEvent!.track_code,
                  },
                }
              : {}),
          // Slice 3: explicit tyre override wins over the auto oval/non-oval
          // override at run time. Omitted entirely for the default path.
          ...(tyreType ? { tire_override: { type: tyreType } } : {}),
          // Lever-set v1: opt AutoResearch into extra per-candidate levers.
          // Omitted entirely when neither is enabled (dampers-only default).
          ...((allowTyreLever || allowThirdGapLever)
            ? {
                ar_levers: [
                  ...(allowTyreLever ? (["tire"] as const) : []),
                  ...(allowThirdGapLever ? (["third_gap"] as const) : []),
                ],
              }
            : {}),
        },
        source_event: ownSetupTrack ? null : selectedEvent!.event_id,
        name: studyName.trim() || null,
      });
      onCreated(test.id);
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="card form-card test-create-panel"
    >
      <div className="test-create-head">
        <div>
          <p className="eyebrow">Virtual Rig Study</p>
          <h3>New Virtual Rig Study</h3>
        </div>
        {trackInputs != null && (
          <div className="test-create-counts" aria-label="Venue counts">
            <span>
              <strong>{trackInputs.filter((t) => t.runnable).length}</strong>
              {" of "}
              <strong>{trackInputs.length}</strong> venues ready
            </span>
          </div>
        )}
      </div>

      <div className="test-create-grid">
        <div className="test-create-fields">
          <div className="form-row">
            <label htmlFor="wiz-name">Study name</label>
            <input
              id="wiz-name"
              type="text"
              value={studyName}
              maxLength={120}
              placeholder={selectedEvent?.event_id ?? "Study name"}
              onChange={(e) => {
                setStudyNameEdited(true);
                setStudyName(e.target.value);
              }}
            />
          </div>

          <div className="form-row">
            <label htmlFor="wiz-track-search">Track input for rig actuation</label>
            {trackInputs == null && (
              <span className="muted">Loading tracks…</span>
            )}
            {eventsState.kind === "error" && (
              <span className="error-text">
                Failed to load cached events: {eventsState.message}
              </span>
            )}
            {trackInputs != null && (
              <div className="ti-track-control">
                <input
                  id="wiz-track-search"
                  className="select ti-search"
                  type="text"
                  placeholder="Search tracks…"
                  value={trackSearch}
                  onChange={(e) => setTrackSearch(e.target.value)}
                />
                <div className="ti-track-list" role="listbox" aria-label="Track input">
                  {currentTracks.length > 0 && (
                    <div className="ti-group-label">Current 2026</div>
                  )}
                  {currentTracks.map((t) => (
                    <TrackInputRow
                      key={t.key}
                      t={t}
                      selected={t.key === selectedTrackKey}
                      onSelect={() => chooseTrack(t)}
                    />
                  ))}
                  {validationTracks.length > 0 && (
                    <div className="ti-group-label">Validation / off-calendar</div>
                  )}
                  {validationTracks.map((t) => (
                    <TrackInputRow
                      key={t.key}
                      t={t}
                      selected={t.key === selectedTrackKey}
                      onSelect={() => chooseTrack(t)}
                    />
                  ))}
                  {currentTracks.length === 0 && validationTracks.length === 0 && (
                    <div className="muted ti-empty">
                      {trackInputs.length === 0
                        ? "No track inputs available."
                        : `No tracks match “${trackSearch}”.`}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {selectedTrack && (
            <div className="form-row ti-source-panel" data-testid="ti-source-panel">
              {selectedTrackSources.length > 0 ? (
                // Option-3 de-clutter (Joe 2026-06-10): the campaign is
                // auto-selected (recommended default); the choice only
                // surfaces behind an advanced disclosure for deliberate
                // multi-campaign comparisons.
                <details className="ti-source-advanced" data-testid="ti-source-advanced">
                  <summary className="muted ti-source-label">
                    Source campaign: <span className="mono">{selectedEventId || "—"}</span>
                    {selectedTrackSources.length > 1
                      ? ` (advanced — ${selectedTrackSources.length} available)`
                      : " (advanced)"}
                  </summary>
                  <div className="ti-source-list" role="radiogroup" aria-label="Source campaign">
                    {selectedTrackSources.map((src, i) => (
                      <SourceRow
                        key={src.source_event}
                        src={src}
                        isDefault={
                          i === 0
                          && selectedTrack.recommended_default?.source_event === src.source_event
                        }
                        selected={selectedEventId === src.source_event}
                        cached={eventById(src.source_event)}
                        onSelect={() => chooseEvent(src.source_event)}
                      />
                    ))}
                  </div>
                </details>
              ) : ownSetupTrack ? (
                <div className="ti-replay-donor" data-testid="ti-own-setup">
                  <span className="muted" style={{ fontSize: "11px" }}>
                    Rig actuation comes from {selectedTrack.track_code}&apos;s
                    ingested track-replay lap. The car platform comes from
                    {" "}{selectedTrack.track_code}&apos;s <strong>own setup
                    sheet</strong> (staged from the engineering share) — no
                    donor campaign needed.
                  </span>
                  <select
                    aria-label="Own setup sheet"
                    data-testid="wiz-own-setup"
                    className="select"
                    value={selectedSetupId}
                    onChange={(e) => setSelectedSetupId(e.target.value)}
                  >
                    {ownSetups.map((s) => (
                      <option key={s.setup_id} value={s.setup_id}>
                        {s.label || s.setup_id}
                        {s.car ? ` — car #${s.car}` : ""}
                        {s.is_race_event ? "" : " (test/DiL)"}
                      </option>
                    ))}
                  </select>
                </div>
              ) : replayOnlyTrack ? (
                <div className="ti-replay-donor" data-testid="ti-replay-donor">
                  <span className="muted" style={{ fontSize: "11px" }}>
                    Rig actuation comes from {selectedTrack.track_code}&apos;s
                    ingested track-replay lap. Pick a <strong>setup donor</strong> —
                    the campaign whose car platform (setup XLSM: springs, dampers,
                    geometry) the virtual rig carries on this track. The donor is
                    stamped in the Study&apos;s provenance.
                  </span>
                  <select
                    aria-label="Setup donor campaign"
                    data-testid="wiz-setup-donor"
                    className="select"
                    value={
                      donorCandidates.some((e) => e.event_id === selectedEventId)
                        ? selectedEventId
                        : ""
                    }
                    onChange={(e) => chooseEvent(e.target.value)}
                  >
                    <option value="">Choose a setup-donor campaign…</option>
                    {donorCandidates.map((ev) => (
                      <option key={ev.event_id} value={ev.event_id}>
                        {ev.event_id} ({ev.track_code.toUpperCase()} setup)
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="event-warning" data-testid="ti-no-source">
                  {trackSourceMessage(selectedTrack)}
                </div>
              )}
            </div>
          )}

          {eventsState.kind === "ready" && labEvents.length > 0 && (
            <details className="event-lab-disclosure">
              <summary>Rig characterisation / lab mode ({labEvents.length})</summary>
              <div className="event-lab-body">
                <p className="event-lab-warning">
                  These inputs are runnable or warmable, but they are not
                  normal Study-ready track events. R0 fidelity is not
                  guaranteed.
                </p>
                <select
                  aria-label="Rig characterisation / lab mode"
                  className="select"
                  value={labEvents.some((e) => e.event_id === selectedEventId) ? selectedEventId : ""}
                  onChange={(e) => chooseEvent(e.target.value)}
                >
                  <option value="">Choose a lab input</option>
                  {labEvents.map((ev) => (
                    <option key={ev.event_id} value={ev.event_id}>
                      {ev.event_id}  ({labDisclosureReason(ev)})
                    </option>
                  ))}
                </select>
              </div>
            </details>
          )}

          {replayOnlyTrack ? (
            <div className="form-row" data-testid="wiz-replay-implicit">
              <span className="muted" style={{ fontSize: "11px" }}>
                This Study drives the rig from the track-replay lap (replay-only
                track — no rig recording exists, so R0 reports no R²).
              </span>
            </div>
          ) : replayAvailable ? (
            <div className="form-row">
              <label htmlFor="wiz-replay">
                <input
                  id="wiz-replay"
                  type="checkbox"
                  checked={useReplay}
                  onChange={(e) => setUseReplay(e.target.checked)}
                  data-testid="wiz-track-replay"
                />{" "}
                Drive from track-replay lap
              </label>
              <span className="muted" style={{ fontSize: "11px" }}>
                Run the rig from {selectedEvent?.track_code.toUpperCase()}'s
                recorded track lap (no R²: there's no rig recording to compare).
              </span>
            </div>
          ) : !replayGloballyAvailable ? (
            // Phase 4: make the cloud "unavailable" state explicit rather than
            // silently omitting the option.
            <div className="form-row" data-testid="wiz-replay-unavailable">
              <span className="muted" style={{ fontSize: "11px" }}>
                Track replay unavailable on this deployment — the recorded-lap
                cache isn't present here. Runs use cached seven-post actuation.
              </span>
            </div>
          ) : null}

          <div className="test-create-actions">
            <button
              type="submit"
              className="btn primary"
              disabled={
                submitting
                || (ownSetupTrack
                  ? !selectedSetupId
                  : !selectedEvent || !selectedEvent.selectable)
              }
            >
              {submitting ? "Creating…" : "Create Study + Run Reference"}
            </button>
            {ownSetupTrack && selectedSetupId ? (
              <span className="muted mono">
                {`R0 will replay ${selectedTrack?.track_code}'s lap with its own setup `}
                {ownSetups.find((s) => s.setup_id === selectedSetupId)?.event_label
                  || selectedSetupId}
              </span>
            ) : selectedEvent && selectedEvent.selectable ? (
              <span className="muted mono">
                {replayOnlyTrack && selectedTrack?.track_code
                  ? `R0 will replay ${selectedTrack.track_code}'s lap with setup donor ${selectedEvent.event_id}`
                  : `Reference run R0 will run from ${selectedEvent.event_id}`}
              </span>
            ) : selectedTrack && !selectableSourceExists ? (
              <span className="muted" data-testid="ti-create-blocked">
                {replayOnlyTrack
                  ? "Pick a setup-donor campaign to create this replay Study."
                  : selectedTrackSources.length === 0
                    ? `${selectedTrack.display_name} has no rig source — ${trackSourceMessage(selectedTrack)}`
                    : `${selectedTrack.display_name}'s recommended source isn't cached or selectable on this deployment.`}
              </span>
            ) : null}
          </div>
        </div>

        <aside className="event-summary-panel">
          {selectedEvent || (ownSetupTrack && selectedTrack) ? (
            <>
              <div className="event-summary-top">
                <div>
                  <p className="eyebrow">Selected actuation</p>
                  <strong className="event-id">
                    {selectedEvent
                      ? selectedEvent.event_id
                      : `${selectedTrack!.track_code} — own setup`}
                  </strong>
                </div>
                {selectedEvent ? (
                  <span className={`event-state ${selectedEvent.study_ready ? "study-ready" : "lab"}`}>
                    {eventReadinessLabel(selectedEvent)}
                  </span>
                ) : (
                  <span className="event-state study-ready">study ready</span>
                )}
              </div>
              <div className="study-create-cards" data-testid="create-input-cards">
                <InputCard
                  index={1}
                  title="Track input"
                  testid="create-card-actuation"
                  value={
                    recipe
                      ? recipe.actuation.summary
                      : selectedEvent
                        ? <><strong>{selectedEvent.track_code}</strong>{" · "}{selectedEvent.recording_count} rec</>
                        : <strong>{selectedTrack!.track_code} replay lap</strong>
                  }
                  affordance={
                    <span className="study-tag">
                      {recipe?.actuation.kind === "replay"
                        ? "replay"
                        : recipe?.actuation.kind === "synthetic"
                          ? "synthetic"
                          : "event"}
                    </span>
                  }
                />
                <InputCard
                  index={2}
                  title="Car platform"
                  testid="create-card-platform"
                  value={
                    recipe
                      ? recipe.platform.summary
                      : selectedEvent
                        ? selectedEvent.setup_xlsm_present ? "setup XLSM present" : "(from event)"
                        : "own setup sheet (staged)"
                  }
                  affordance={
                    <span className="study-tag">
                      {recipe?.platform.donor
                        ? "donor"
                        : recipe?.platform.kind === "own_event_setup" || ownSetupTrack
                          ? "own setup"
                          : "from event"}
                    </span>
                  }
                />
                <InputCard
                  index={3}
                  title="Tyre sidewall"
                  testid="create-card-tyre"
                  value={
                    <>
                      <select
                        data-testid="create-tyre-select"
                        value={tyreType}
                        onChange={(e) => setTyreType(e.target.value)}
                      >
                        <option value="">event-derived default</option>
                        {tyreModels.map((m) => (
                          <option key={m.type} value={m.type}>{m.type}</option>
                        ))}
                      </select>
                      {recipe && recipe.tyre.kind !== "event_measured" && (
                        <span
                          className="muted"
                          style={{ fontSize: "11px", display: "block" }}
                          data-testid="wiz-tyre-class-hint"
                        >
                          {recipe.tyre.summary}
                        </span>
                      )}
                    </>
                  }
                  affordance={
                    <span className="study-tag">
                      {recipe?.tyre.kind === "class_median" ? "class" : "override ▾"}
                    </span>
                  }
                />
                <InputCard
                  index={4}
                  title="Damper config"
                  testid="create-card-damper"
                  value={
                    <span className="muted">
                      {recipe ? recipe.damper.summary : "PVP clicks · F-V overlay (experimental)"}
                    </span>
                  }
                  affordance={<span className="study-tag">edit later</span>}
                />
              </div>
              {/* Lever-set v1: opt AutoResearch into extra per-candidate
                  levers. Off by default keeps the action space to dampers
                  (clicks + F-V magnitude). Each box widens what AutoResearch
                  may vary while searching. */}
              <fieldset
                className="ar-levers"
                data-testid="create-ar-levers"
                style={{
                  marginTop: "12px",
                  border: "1px solid var(--border, #333)",
                  borderRadius: "6px",
                  padding: "10px 12px",
                }}
              >
                <legend style={{ fontSize: "12px", padding: "0 4px" }}>
                  AutoResearch levers
                </legend>
                <p className="muted" style={{ fontSize: "12px", margin: "0 0 8px" }}>
                  Beyond damper clicks and F-V magnitude, AutoResearch can vary
                  these per candidate while it searches. Each one widens the
                  study's action space; leave both off for a dampers-only study.
                </p>
                <label
                  className="ar-lever-option"
                  style={{ display: "flex", gap: "8px", marginBottom: "8px" }}
                >
                  <input
                    type="checkbox"
                    data-testid="create-ar-lever-tire"
                    checked={allowTyreLever}
                    onChange={(e) => setAllowTyreLever(e.target.checked)}
                  />
                  <span>
                    Allow AutoResearch to change the tyre model
                    <span
                      className="muted"
                      style={{ fontSize: "11px", display: "block" }}
                    >
                      Each candidate may swap to another catalogue tyre, on top
                      of the study's baseline tyre above.
                    </span>
                  </span>
                </label>
                <label
                  className="ar-lever-option"
                  style={{ display: "flex", gap: "8px" }}
                >
                  <input
                    type="checkbox"
                    data-testid="create-ar-lever-third-gap"
                    checked={allowThirdGapLever}
                    onChange={(e) => setAllowThirdGapLever(e.target.checked)}
                  />
                  <span>
                    Allow AutoResearch to adjust third-element gaps
                    <span
                      className="muted"
                      style={{ fontSize: "11px", display: "block" }}
                    >
                      Each candidate may move the front and/or rear third-element
                      installation gap (0-30 mm); the spring rate is unchanged.
                    </span>
                  </span>
                </label>
              </fieldset>
              {selectedEvent && !selectedEvent.selectable && (
                <div className="event-warning">
                  {selectedEvent.rejection_reasons.join("; ")}
                </div>
              )}
              {selectedEvent && !selectedEvent.study_ready && selectedEvent.selectable && (
                <div className="event-warning">
                  Lab mode: inputs are present, but this is not a Study-ready
                  track event ({eventReadinessLabel(selectedEvent).toLowerCase()}).
                  R0 physics fit is not guaranteed. {selectedEvent.gate_detail}
                </div>
              )}
              {selectedEvent?.input_info_missing && (
                <div className="event-warning" style={{ color: "#e0b341" }}>
                  No InputInfo.mat for this event — the rig will fall back to
                  the setup XLSM's motion ratios (runnable; no R² penalty).
                </div>
              )}
              {selectedEvent && selectedEvent.ingest_status === "needs_ingest" && (
                <div className="event-ingest">
                  <button
                    type="button"
                    className="btn secondary cached-event-ingest-button"
                    disabled={ingestingId === selectedEvent.event_id}
                    onClick={async () => {
                      const id = selectedEvent.event_id;
                      setIngestingId(id);
                      setIngestError(null);
                      try {
                        await ingestCachedEvent(id);
                      } catch (e) {
                        setIngestError({
                          eventId: id,
                          message: e instanceof Error ? e.message : String(e),
                        });
                      } finally {
                        setIngestingId(null);
                      }
                      // Always refetch — even on error, the backend
                      // may have reclassified (e.g. 409 stale-cached).
                      try {
                        const refreshed = await listCachedEvents();
                        setEventsState({ kind: "ready", events: refreshed });
                      } catch {
                        // listCachedEvents failure is unusual; leave
                        // state alone so the engineer can retry.
                      }
                    }}
                  >
                    {ingestingId === selectedEvent.event_id ? "Ingesting…" : "Ingest"}
                  </button>
                  {ingestError?.eventId === selectedEvent.event_id && (
                    <span className="error-text cached-event-ingest-error">
                      {ingestError.message}
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="muted">Select a track input to inspect its cached source.</p>
          )}
        </aside>
      </div>

      {error && (
        <div className="error-text test-create-error">{error}</div>
      )}
    </form>
  );
}
